import React, { useState, useEffect } from "react";
import "../styles/purchase-popup.css";

interface PurchasePopupProps {
  isOpen: boolean;
  itemName: string;
  onConfirm: (
    purchasedByName: string,
    purchasedById?: string,
    note?: string
  ) => void;
  onCancel: () => void;
  defaultPurchasedBy?: string;
}

const PurchasePopup: React.FC<PurchasePopupProps> = ({
  isOpen,
  itemName,
  onConfirm,
  onCancel,
  defaultPurchasedBy = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [purchasedByName, setPurchasedByName] = useState(defaultPurchasedBy);
  const [purchasedById, setPurchasedById] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<{ purchasedByName?: string }>({});

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimating(false);
      setPurchasedByName(defaultPurchasedBy);
      setPurchasedById("");
      setNote("");
      setErrors({});
    } else if (isVisible) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible, defaultPurchasedBy]);

  const handleConfirm = () => {
    // Validate form
    if (!purchasedByName.trim()) {
      setErrors({ purchasedByName: "Please enter your name" });
      return;
    }

    onConfirm(
      purchasedByName.trim(),
      purchasedById.trim() || undefined,
      note.trim() || undefined
    );
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    } else if (e.key === "Enter" && e.ctrlKey) {
      handleConfirm();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`purchase-popup-overlay ${isAnimating ? "fade-out" : ""}`}
      onClick={handleBackdropClick}
    >
      <div
        className={`purchase-popup ${isAnimating ? "slide-out" : ""}`}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="purchase-popup-title"
      >
        <div className="purchase-popup-header">
          <h3 id="purchase-popup-title" className="purchase-popup-title">
            Mark as Purchased
          </h3>
        </div>

        <div className="purchase-popup-body">
          <p className="purchase-popup-message">
            You're marking <strong>"{itemName}"</strong> as purchased.
          </p>

          <div className="form-group">
            <label htmlFor="purchased-by-name">
              Your Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="purchased-by-name"
              value={purchasedByName}
              onChange={(e) => {
                setPurchasedByName(e.target.value);
                if (errors.purchasedByName) {
                  setErrors({ ...errors, purchasedByName: undefined });
                }
              }}
              placeholder="Enter your name..."
              className={errors.purchasedByName ? "error" : ""}
              autoFocus
            />
            {errors.purchasedByName && (
              <span className="error-message">{errors.purchasedByName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="purchased-by-id">Invitation Code (Optional)</label>
            <input
              type="text"
              id="purchased-by-id"
              value={purchasedById}
              onChange={(e) => setPurchasedById(e.target.value)}
              placeholder="Enter your invitation code..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="note">Note (Optional)</label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a personal note or message..."
              rows={3}
              maxLength={500}
            />
            <span className="char-count">{note.length}/500</span>
          </div>
        </div>

        <div className="purchase-popup-footer">
          <button
            type="button"
            className="purchase-popup-btn purchase-popup-btn-cancel"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="purchase-popup-btn purchase-popup-btn-confirm"
            onClick={handleConfirm}
          >
            Mark as Purchased
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchasePopup;
